import { useEffect, useRef } from 'react';
import { useDesignStore } from '../store/designStore';
import { useHistoryStore, HISTORY_SNAPSHOT_PROPS, restoreCanvasJsonSilently } from '../store/historyStore';
import type { DesignSide } from '../store/designStore';
import { registerCurvedText } from '../lib/fabric.curvedText';
import { resolveCssVar, hexToRgba } from '../lib/themeColor';

let fabricRegistered = false;

export function useFabricCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  side: DesignSide | undefined
) {
  const fabricRef  = useRef<any | null>(null);
  const {
    registerCanvas, unregisterCanvas, setSelectedObject,
    cacheSideColorCountJson, resolveActiveColorCountKey,
    selectedPrintColorOption, selectedPrintColorByPosition, isRestoringSavedDesign,
    markSideDirty,
  } = useDesignStore();
  const { pushSnapshot, init: initHistory } = useHistoryStore();
  // Tracks which color-count key the currently-mounted canvas was last
  // snapshotted/restored under — lets the count-switch effect below know
  // what to snapshot FROM when selectedPrintColorOption/
  // selectedPrintColorByPosition changes while this same side stays mounted.
  // Reset on every side mount (see the mount effect) so it always starts
  // seeded with the count that was actually just restored, not a stale value
  // left over from a different side.
  const lastAppliedCountKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !side) return;
    // `import('fabric')` is async, so this effect's cleanup can run (e.g. React
    // StrictMode's deliberate double-invoke on every mount, or just switching away
    // to the 3D Preview tab and back fast) *before* this resolves. Without bailing
    // out here, the canvas-construction code below still runs against the same
    // `canvasRef.current` DOM node a second Fabric instance may already be
    // wrapping — Fabric's constructor wraps whatever element it's given in a new
    // `canvas-container` div, so the result is one Fabric instance's wrapper
    // nested inside the other's, with each instance's separate upper-canvas (the
    // interactive/selection layer) left stacked in the DOM. Clicks then land on
    // whichever happens to be on top while the visible content renders from the
    // other — confirmed live: editing an object via the right-side property
    // panel after a 3D Preview round-trip updated the input field (reading from
    // the orphaned instance's object) but never visibly changed the canvas (which
    // was rendering the *other* instance), making the edit look like it silently
    // "reset" the moment you re-checked it.
    let cancelled = false;

    const loadFabric = async () => {
      const { fabric } = await import('fabric');
      if (cancelled) return;

      if (!fabricRegistered) {
        registerCurvedText(fabric);
        fabricRegistered = true;
      }

      // Fabric option objects are plain color strings set once at construction
      // (and mutated directly on the prototype below) — they can't reference
      // var(--selection) the way a React style prop can, so resolve it here.
      const selectionColor = resolveCssVar('--selection', '#16bcdc');

      const fc = new fabric.Canvas(canvasRef.current!, {
        preserveObjectStacking: true,
        selection: true,
        width:  side.canvasWidth,
        height: side.canvasHeight,
        // Selection style — dashed bounding box, filled square handles
        selectionColor:       hexToRgba(selectionColor, 0.06),
        selectionBorderColor: selectionColor,
        selectionLineWidth:   1,
      });

      // Per-object selection handle style — set directly on prototype (not via .set())
      Object.assign(fabric.Object.prototype, {
        cornerStyle:        'rect',
        cornerColor:        selectionColor,
        cornerSize:         8,
        borderColor:        selectionColor,
        borderDashArray:    [4, 4],
        transparentCorners: false,
      });

      fabricRef.current = fc;
      // Store original dimensions for fitToViewport calculations
      (fc as any)._originalWidth  = side.canvasWidth;
      (fc as any)._originalHeight = side.canvasHeight;
      registerCanvas(side.sideKey, fc);

      // DesignerWorkspace mounts one <canvas> reused across sides — this is a
      // brand-new, blank Fabric instance every time a side becomes active,
      // including when switching back to a side already edited this session.
      // Restore whatever was cached for it (either from a prior switch-away,
      // via this hook's own cleanup below, or from loading an existing saved
      // multi-side design) — without this, revisiting a side wipes it.
      const toSnapshotJson = () => JSON.stringify(fc.toJSON(HISTORY_SNAPSHOT_PROPS));

      // Resolve which color-count "slot" this side is currently under, and
      // look up that slot's own snapshot — NOT the old flat per-side cache,
      // which can't tell two different Print Color counts' content apart.
      // Fall back to the legacy flat sideJsonCache ONLY when this side has no
      // nested-cache entries at all yet (i.e. a design saved before this
      // feature existed, or a side never before snapshotted under the new
      // shape) — this is the back-compat path; loadFromTemplate's own shim
      // already normalizes legacy saved-design data into the nested cache
      // before this hook ever mounts, so this fallback mainly covers a side
      // whose very first visit this session hasn't gone through a save/load
      // round-trip at all.
      const countKey = resolveActiveColorCountKey(side);
      lastAppliedCountKeyRef.current = countKey;
      const state = useDesignStore.getState();
      const nestedForSide = state.sideColorCountJsonCache[side.sideKey];
      const cached = nestedForSide?.[countKey]
        ?? (nestedForSide === undefined ? state.sideJsonCache[side.sideKey] : undefined);
      if (cached) {
        fc.loadFromJSON(cached, () => {
          fc.renderAll();
          // Seed history with this side's starting state once it's actually loaded —
          // otherwise undo has nothing to fall back to after the first edit (see
          // historyStore.ts's `init`).
          initHistory(toSnapshotJson());
        });
      } else {
        initHistory(toSnapshotJson());
      }

      // Track selected object
      fc.on('selection:created',  (e: any) => setSelectedObject(e.selected?.[0] ?? null));
      fc.on('selection:updated',  (e: any) => setSelectedObject(e.selected?.[0] ?? null));
      fc.on('selection:cleared',  ()        => setSelectedObject(null));

      // Dragging a plain text/textbox's resize handles scales it via scaleX/scaleY —
      // fontSize itself never changes. CurvedText already bakes scale into its own
      // fontSize/diameter internally (see fabric.curvedText.ts's `_set` override), but
      // nothing did the equivalent for ordinary text, so PropertyPanel's "Size" field
      // (which reads `obj.fontSize` directly) went stale and silently lied about the
      // true rendered size the moment a user dragged a corner handle — and typing a new
      // absolute size afterward would stack on top of the hidden scale multiplier
      // instead of replacing it. Registered before the snapshot listener below so the
      // history snapshot captures the corrected values, not the pre-bake scale.
      fc.on('object:modified', (e: any) => {
        const obj = e.target;
        if (!obj || !['text', 'textbox', 'i-text'].includes(obj.type)) return;
        const scaleX = obj.scaleX ?? 1;
        const scaleY = obj.scaleY ?? 1;
        if (scaleX === 1 && scaleY === 1) return;
        obj.set({
          fontSize: Math.round((obj.fontSize ?? 20) * scaleY),
          width: (obj.width ?? 0) * scaleX,
          scaleX: 1,
          scaleY: 1,
        });
        obj.setCoords();
      });

      // History snapshots — skipped while `__suppressHistory` is set (during
      // undo/redo, or any other programmatic restore via `restoreCanvasJsonSilently`).
      // `loadFromJSON` clears the canvas via Fabric's real `remove()` before
      // re-adding the restored objects, which fires a genuine 'object:removed'
      // per pre-existing object — without this guard, every undo/redo would
      // queue a snapshot of that momentarily-*empty* in-between state and,
      // 500ms later, silently overwrite `future` out from under a Redo click.
      // See historyStore.ts's `restoreCanvasJsonSilently` for full detail.
      const snapshot = () => { if (!fc.__suppressHistory) pushSnapshot(toSnapshotJson()); };
      fc.on('object:added',    snapshot);
      fc.on('object:removed',  snapshot);
      fc.on('object:modified', snapshot);

      // Marks this side dirty (see designStore.ts's own comment on
      // dirtySideKeys for the real bug this closes: a stale embossing/
      // embroidery Preview-tab render with no indication anything changed).
      // Same __suppressHistory guard as the snapshot handler above — undo/
      // redo and programmatic restores (restoreCanvasJsonSilently) must not
      // spuriously mark a side dirty when nothing the customer actually did
      // changed its saved content.
      const markDirty = () => { if (!fc.__suppressHistory) markSideDirty(side.sideKey); };
      fc.on('object:added',    markDirty);
      fc.on('object:removed',  markDirty);
      fc.on('object:modified', markDirty);

      // Initial fit-to-viewport is handled by DesignerApp's fitToViewport effect, which
      // re-fits as soon as activeCanvas is set AND again once the product image's real
      // size is known — doing it here too (on a fixed timer, canvas-dims-only) used to
      // race with that and could overwrite a correct image-aware fit with a wrong one.
    };

    loadFabric();

    return () => {
      cancelled = true;
      if (fabricRef.current) {
        // Snapshot before disposing — this is the only chance to preserve
        // this side's content; once dispose() runs, the Fabric instance and
        // everything drawn on it is gone for good. Re-resolve the count key
        // (rather than reusing a captured mount-time value) since the count
        // switch effect below keeps lastAppliedCountKeyRef current for
        // exactly this reason — the customer may have changed Print Color
        // while this side was active, and this snapshot must land under
        // whatever count was ACTUALLY on screen a moment ago.
        const finalCountKey = resolveActiveColorCountKey(side);
        cacheSideColorCountJson(side.sideKey, finalCountKey, fabricRef.current.toJSON(HISTORY_SNAPSHOT_PROPS));
        unregisterCanvas(side.sideKey);
        fabricRef.current.dispose();
        fabricRef.current = null;
        // `selectedObject` otherwise survives this disposal untouched — it's
        // a reference to a now-garbage object that belonged to the disposed
        // canvas, not the freshly-loaded one created on the next mount (e.g.
        // switching away to the 3D Preview tab and back). Left unset,
        // PropertyPanel keeps showing that orphan as "selected": its fields
        // (e.g. font size) still read/write correctly against the orphan, so
        // edits appear to apply (the input updates) while the real, visible
        // object on the live canvas never changes — confirmed live, this is
        // exactly what made text-size edits look like they "reset" after a
        // visit to 3D Preview, when the edit had actually just landed on a
        // disconnected, never-rendered object the whole time.
        setSelectedObject(null);
      }
    };
  }, [side?.sideKey]);

  // Color-count switch WITHOUT a side switch — e.g. the customer applies a
  // different Print Color choice in ProductOptionsModal while this same
  // side's canvas stays mounted. The mount/unmount effect above only fires on
  // side changes (side?.sideKey dependency), so it can't cover this case;
  // disposing/remounting the whole Fabric canvas on every count change would
  // also be needlessly heavy (loses zoom/pan state, refires every listener
  // setup). Instead: snapshot the side's current on-screen content under the
  // OLD count key, then restore (or blank) it under the NEW one, in place.
  useEffect(() => {
    if (!side) return;
    // useDesignWip.ts/useSharedDesignLoader.ts set selectedPrintColorOption/
    // selectedPrintColorByPosition (Phase 1) BEFORE calling loadFromTemplate
    // with the real saved canvas content (Phase 2) — reacting here in
    // between would snapshot/restore against a canvas that hasn't received
    // its real data yet, pushing a spurious "blank" history entry that
    // Phase 2's own restore then silently overwrites on screen (so it's
    // invisible to the customer) but leaves polluting undo history. Both
    // loader hooks set isRestoringSavedDesign true for the duration of their
    // restore sequence specifically so this effect can skip itself here.
    if (isRestoringSavedDesign) return;
    const countKey = resolveActiveColorCountKey(side);
    const fc = fabricRef.current;
    // Not yet mounted — the mount effect's own initial read above already
    // resolves the CURRENT key correctly, so there's nothing to reconcile.
    if (!fc) return;
    // No real change: either this is this effect's own first run right after
    // mount (lastAppliedCountKeyRef was already seeded to the same value by
    // the mount effect), or a DIFFERENT position's color count changed and
    // this side doesn't belong to that position (resolveActiveColorCountKey
    // only reads what THIS side's own position maps to, so it's already
    // naturally a no-op for unrelated positions).
    if (countKey === lastAppliedCountKeyRef.current) return;

    const previousKey = lastAppliedCountKeyRef.current;
    if (previousKey !== null) {
      cacheSideColorCountJson(side.sideKey, previousKey, fc.toJSON(HISTORY_SNAPSHOT_PROPS));
    }

    const cached = useDesignStore.getState().sideColorCountJsonCache[side.sideKey]?.[countKey];
    // Use restoreCanvasJsonSilently (not a plain loadFromJSON) for the same
    // reason the mount effect and loadFromTemplate both do — Fabric's
    // clear() step inside a raw loadFromJSON fires real 'object:removed'
    // events per pre-existing object, which this hook's own history listener
    // (fc.on('object:removed', snapshot) above) would otherwise record as a
    // genuine user edit, corrupting undo/redo.
    restoreCanvasJsonSilently(fc, cached ?? { objects: [], background: '' }).then(() => {
      pushSnapshot(JSON.stringify(fc.toJSON(HISTORY_SNAPSHOT_PROPS)));
      fc.fire('history:restored');
    });

    lastAppliedCountKeyRef.current = countKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side, selectedPrintColorOption, selectedPrintColorByPosition, isRestoringSavedDesign]);

  return fabricRef;
}
